import { useState } from 'react';
import { Check, Clock, MapPin, Plus, Stethoscope } from 'lucide-react';
import type { CheckOption } from '@/types';
import { medicalTestList, planAddons, planFeatures, priceSelection } from '@/utils/checkOptions';
import { stashSelection } from '@/utils/planSelection';
import { EVENTS, track } from '@/lib/analytics';
import Sheet from './Sheet';

const TOP_FEATURE_COUNT = 3;

/**
 * Short audience labels for the compact cards. Intentionally terser than the
 * landing page's "Recommended for …" copy — these sit under a plan name in a
 * card roughly a third of the width.
 */
const PLAN_AUDIENCE: Record<string, string> = {
    standard: 'Domestic staff',
    enhanced: 'Most business hires',
    pro: 'Executive & finance roles',
};

function formatNaira(price: number): string {
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
    }).format(price || 0);
}

interface PlanRailProps {
    plans: CheckOption[];
    isLoading: boolean;
    onChoosePlan: (plan: CheckOption) => void;
}

/**
 * The pricing block.
 *
 * On mobile the three plans are a horizontal snap rail rather than a stack —
 * three stacked cards would push the callback form two screens down and break
 * the single-view promise of this page.
 */
export default function PlanRail({ plans, isLoading, onChoosePlan }: PlanRailProps) {
    const [expanded, setExpanded] = useState<CheckOption | null>(null);
    // planId → the feature ids kept. Absent means untouched, i.e. the whole
    // plan. Held here rather than in the card so a choice made in the detail
    // sheet survives closing it, and shows on the card behind.
    const [picked, setPicked] = useState<Record<string, string[]>>({});

    const selectedFor = (plan: CheckOption) => picked[plan.id] ?? planFeatures(plan).map((f) => f.id);

    const toggleFeature = (plan: CheckOption, featureId: string) =>
        setPicked((prev) => {
            const current = prev[plan.id] ?? planFeatures(plan).map((f) => f.id);
            return {
                ...prev,
                [plan.id]: current.includes(featureId)
                    ? current.filter((id) => id !== featureId)
                    : [...current, featureId],
            };
        });

    const takeWholePlan = (plan: CheckOption) =>
        setPicked((prev) => ({ ...prev, [plan.id]: planFeatures(plan).map((f) => f.id) }));

    /**
     * The CTA here books a callback rather than going to checkout, but the
     * choice is still worth keeping: it is stashed so that if they go on to
     * sign up, their picked features come back pre-selected.
     */
    const choosePlan = (plan: CheckOption) => {
        const features = planFeatures(plan);
        if (features.length > 0) {
            const selected = selectedFor(plan);
            stashSelection({
                checkType: plan.id,
                features: features.filter((f) => selected.includes(f.id)).map((f) => f.id),
                addons: [],
            });
        }
        onChoosePlan(plan);
    };

    const openDetails = (plan: CheckOption) => {
        setExpanded(plan);
        track(EVENTS.PLAN_DETAILS_OPENED, { planId: plan.id, planName: plan.name });
    };

    return (
        <section aria-labelledby="plans-heading" className="flex min-w-0 flex-col gap-3 xl:gap-4">
            {/* The differentiator that justifies the price — deliberately inside the
                pricing block, where the number is, rather than up in the headline. */}
            <p className="flex items-start gap-2.5 rounded-xl border border-[#FF6B35]/25 bg-[#FFF3EE] px-3.5 py-3 text-[13px] leading-snug text-[#4A463F] sm:text-sm xl:px-4 xl:py-3.5 xl:text-[15px]">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6B35] xl:h-[18px] xl:w-[18px]" aria-hidden="true" />
                <span>
                    We deploy <span className="font-semibold text-[#15171C]">boots on the ground</span> to gather
                    intelligence and verify critical information.
                </span>
            </p>

            {isLoading ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="h-[230px] animate-pulse rounded-2xl border border-[#E4E1DA] bg-[#EFEDE8] xl:h-[260px]"
                        />
                    ))}
                </div>
            ) : (
                // Snap rail below `md`. Three columns need ~230px each to stop the
                // feature text wrapping to three lines, so the grid only starts once
                // the row can actually afford it.
                <div
                    className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pt-2 pb-2 sm:-mx-6 sm:px-6 md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0 xl:gap-4"
                    role="list"
                >
                    {plans.map((plan) => {
                        const features = planFeatures(plan);
                        const quote = priceSelection(plan, selectedFor(plan));
                        // The headline follows what they actually kept, so a
                        // customised plan never advertises the full price.
                        const price = features.length > 0 ? quote.total : (plan.employerPrice ?? plan.price);
                        // Priced lines when the API sends them, plain bullets otherwise.
                        const lines =
                            features.length > 0
                                ? features.map((f) => ({ key: f.id, label: f.label, price: f.price }))
                                : plan.features.map((label) => ({ key: label, label, price: 0 }));
                        const top = lines.slice(0, TOP_FEATURE_COUNT);
                        const rest = lines.length - top.length;

                        return (
                            <article
                                role="listitem"
                                key={plan.id}
                                className={`relative flex w-[80vw] max-w-[320px] shrink-0 snap-start flex-col rounded-2xl border bg-white p-4 transition-all sm:w-[46vw] md:w-auto md:max-w-none xl:p-5 ${
                                    plan.popular
                                        ? 'border-[#FF6B35] shadow-[0_8px_30px_-12px_rgba(255,107,53,0.45)]'
                                        : 'border-[#E4E1DA] hover:border-[#C9C4BA] hover:shadow-sm'
                                }`}
                            >
                                {plan.popular && (
                                    <span className="absolute -top-2.5 left-4 rounded-full bg-[#FF6B35] px-2.5 py-0.5 font-mono text-[9px] font-semibold tracking-[0.14em] text-white uppercase xl:text-[10px]">
                                        Popular
                                    </span>
                                )}

                                <h3 className="text-base font-semibold text-[#15171C] xl:text-lg">{plan.name}</h3>
                                {/* Reserves two lines so a wrapping audience label
                                    ("Executive & finance roles") doesn't push one
                                    card's price out of line with its neighbours. */}
                                <p className="mt-0.5 min-h-[2.6em] font-mono text-[10px] leading-snug tracking-wide text-[#A8A399] xl:text-[11px]">
                                    {PLAN_AUDIENCE[plan.id] ?? 'All hires'}
                                </p>

                                <p className="mt-2.5 text-xl leading-none font-bold text-[#15171C] tabular-nums xl:mt-3 xl:text-[28px]">
                                    {formatNaira(price)}
                                </p>

                                {/* Customising happens in the detail sheet — this card
                                    is too narrow for checkboxes without crowding the
                                    price out. It shows the state, and says where to
                                    change it. */}
                                {!quote.isWholePlan && features.length > 0 && (
                                    <p className="mt-1 text-[11px] text-[#8A857C] xl:text-[12px]">
                                        {quote.features.length} of {features.length} checks
                                    </p>
                                )}

                                <ul className="mt-3 flex flex-col gap-2 xl:mt-4 xl:gap-2.5">
                                    {top.map((line) => {
                                        const isOn = features.length === 0 || selectedFor(plan).includes(line.key);
                                        return (
                                            <li
                                                key={line.key}
                                                className={`flex gap-2 text-[12.5px] leading-snug xl:text-[14px] ${
                                                    isOn ? 'text-[#4A463F]' : 'text-[#A8A399] line-through'
                                                }`}
                                            >
                                                <Check
                                                    className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${isOn ? 'text-[#FF6B35]' : 'text-[#D6D2C9]'}`}
                                                    aria-hidden="true"
                                                />
                                                <span className="flex-1">{line.label}</span>
                                                {line.price > 0 && (
                                                    <span className="shrink-0 text-[11px] text-[#A8A399] tabular-nums">
                                                        {formatNaira(line.price)}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="mt-auto pt-3.5 xl:pt-4">
                                    {rest > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => openDetails(plan)}
                                            className="inline-flex items-center gap-1 font-mono text-[10px] tracking-wide text-[#FF6B35] uppercase transition-opacity hover:opacity-70 xl:text-[11px]"
                                        >
                                            <Plus className="h-3 w-3" aria-hidden="true" />
                                            {rest} more — {features.length > 0 ? 'view & customise' : 'read more'}
                                        </button>
                                    )}
                                    {planAddons(plan).length > 0 && (
                                        <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[#8A857C] xl:text-[12px]">
                                            <Stethoscope
                                                className="h-3 w-3 shrink-0 text-[#FF6B35]"
                                                aria-hidden="true"
                                            />
                                            + {planAddons(plan).length} optional add-on
                                            {planAddons(plan).length > 1 ? 's' : ''}
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => choosePlan(plan)}
                                        className="mt-2.5 w-full rounded-xl border border-[#E4E1DA] bg-[#FF6B35] py-2.5 text-[12.5px] font-semibold text-[#FFFFFF] transition-colors hover:border-[#15171C] hover:bg-[#15171C] hover:text-white xl:py-3 xl:text-[14px]"
                                    >
                                        Request a callback
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* Turnaround closes the pricing block: the buyer has just read the
                numbers, and "how long" is the next question. Centred so it reads as
                a note on all three plans rather than a footnote to the last card. */}
            <p className="flex items-center justify-center gap-2 text-center text-[12.5px] leading-snug text-[#6B6862] sm:text-[13.5px] xl:text-sm">
                <Clock className="h-4 w-4 shrink-0 text-[#FF6B35]" aria-hidden="true" />
                <span>
                    Our report is delivered within <span className="font-semibold text-[#15171C]">21 working days</span>
                    .
                </span>
            </p>

            <Sheet
                open={!!expanded}
                onClose={() => setExpanded(null)}
                label={expanded ? `${expanded.name} — everything included` : 'Plan details'}
            >
                {expanded && (
                    <>
                        <h3 className="text-lg font-semibold text-[#15171C]">{expanded.name}</h3>
                        {(() => {
                            const features = planFeatures(expanded);
                            const quote = priceSelection(expanded, selectedFor(expanded));
                            if (features.length === 0) {
                                // A response that predates priced features.
                                return (
                                    <>
                                        <p className="mt-1 text-2xl font-bold text-[#FF6B35] tabular-nums">
                                            {formatNaira(expanded.employerPrice ?? expanded.price)}
                                        </p>
                                        <ul className="mt-4 flex flex-col gap-2.5">
                                            {expanded.features.map((feature) => (
                                                <li
                                                    key={feature}
                                                    className="flex gap-2 text-[13.5px] leading-snug text-[#4A463F]"
                                                >
                                                    <Check
                                                        className="mt-0.5 h-4 w-4 shrink-0 text-[#FF6B35]"
                                                        aria-hidden="true"
                                                    />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                );
                            }

                            const costsMoreThanPlan =
                                !quote.isWholePlan && quote.featuresTotal > (expanded.employerPrice ?? expanded.price);

                            return (
                                <>
                                    <p className="mt-1 text-2xl font-bold text-[#FF6B35] tabular-nums">
                                        {formatNaira(quote.total)}
                                    </p>
                                    {quote.isWholePlan && expanded.separatePrice && expanded.wholePlanSaving ? (
                                        <p className="mt-0.5 text-[12px] text-[#8A857C]">
                                            <span className="line-through">{formatNaira(expanded.separatePrice)}</span>{' '}
                                            bought separately, saving{' '}
                                            <span className="font-semibold text-[#FF6B35]">
                                                {formatNaira(expanded.wholePlanSaving)}
                                            </span>
                                        </p>
                                    ) : null}

                                    <p className="mt-3 text-[12px] text-[#8A857C]">
                                        All included —{' '}
                                        <span className="font-medium text-[#15171C]">tap any line to remove it</span>
                                    </p>

                                    <ul className="mt-2 flex flex-col gap-1">
                                        {features.map((feature) => {
                                            const isOn = selectedFor(expanded).includes(feature.id);
                                            return (
                                                <li key={feature.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleFeature(expanded, feature.id)}
                                                        aria-pressed={isOn}
                                                        className={`flex w-full cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-[#F7F6F3] ${
                                                            isOn ? '' : 'opacity-50 hover:opacity-80'
                                                        }`}
                                                    >
                                                        <span
                                                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                                                                isOn
                                                                    ? 'border-[#FF6B35] bg-[#FF6B35]'
                                                                    : 'border-[#D6D2C9] bg-white'
                                                            }`}
                                                        >
                                                            {isOn && (
                                                                <Check
                                                                    className="h-3 w-3 text-white"
                                                                    strokeWidth={3}
                                                                    aria-hidden="true"
                                                                />
                                                            )}
                                                        </span>
                                                        <span
                                                            className={`flex-1 text-[13.5px] leading-snug text-[#4A463F] ${isOn ? '' : 'line-through'}`}
                                                        >
                                                            {feature.label}
                                                        </span>
                                                        <span className="shrink-0 text-[12px] text-[#A8A399] tabular-nums">
                                                            {formatNaira(feature.price)}
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    {/* Dropping a line loses the whole plan saving, which
                                        often costs more than the line itself. */}
                                    {!quote.isWholePlan && (
                                        <button
                                            type="button"
                                            onClick={() => takeWholePlan(expanded)}
                                            className={`mt-3 flex w-full items-start gap-2 rounded-xl border border-dashed px-3 py-2.5 text-left text-[12px] ${
                                                costsMoreThanPlan
                                                    ? 'border-[#FF6B35] bg-[#FFF3EE]'
                                                    : 'border-[#E4E1DA] bg-[#F7F6F3]'
                                            }`}
                                        >
                                            <span className="text-[#4A463F]">
                                                {costsMoreThanPlan ? (
                                                    <>
                                                        That is{' '}
                                                        {formatNaira(
                                                            quote.featuresTotal -
                                                                (expanded.employerPrice ?? expanded.price),
                                                        )}{' '}
                                                        more than the full {expanded.name} plan.{' '}
                                                        <span className="font-semibold text-[#FF6B35]">
                                                            Take everything
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        Taking every line saves {expanded.wholePlanDiscountPercent ?? 9}
                                                        %.{' '}
                                                        <span className="font-semibold text-[#FF6B35]">
                                                            Restore the full plan
                                                        </span>
                                                    </>
                                                )}
                                            </span>
                                        </button>
                                    )}
                                </>
                            );
                        })()}

                        {/* Optional add-ons, priced separately — kept out of the
                            card face so the headline price stays the price you pay. */}
                        {planAddons(expanded).length > 0 && (
                            <div className="mt-4 rounded-xl border border-[#E4E1DA] bg-[#F7F6F3] p-3.5">
                                <p className="flex items-center gap-2 text-[13.5px] font-semibold text-[#15171C]">
                                    <Stethoscope className="h-4 w-4 shrink-0 text-[#FF6B35]" aria-hidden="true" />
                                    Optional add-ons
                                </p>

                                <ul className="mt-2.5 flex flex-col gap-2.5">
                                    {planAddons(expanded).map((addon) => (
                                        <li key={addon.id} className="flex flex-col gap-0.5">
                                            <div className="flex items-start justify-between gap-3">
                                                <span className="text-[13px] font-medium text-[#15171C]">
                                                    {addon.name}
                                                </span>
                                                <span className="shrink-0 text-[13px] font-bold text-[#FF6B35] tabular-nums">
                                                    +{formatNaira(addon.price)}
                                                </span>
                                            </div>
                                            {/* The medical panel lists its tests rather than a prose description. */}
                                            {addon.id === 'medical_analysis' &&
                                            medicalTestList(expanded.medicalAnalysis).length > 0 ? (
                                                <span className="text-[12px] leading-snug text-[#6B6862]">
                                                    {medicalTestList(expanded.medicalAnalysis).join(', ')}
                                                </span>
                                            ) : (
                                                addon.description && (
                                                    <span className="text-[12px] leading-snug text-[#6B6862]">
                                                        {addon.description}
                                                    </span>
                                                )
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <button
                            type="button"
                            onClick={() => {
                                choosePlan(expanded);
                                setExpanded(null);
                            }}
                            className="mt-5 w-full rounded-xl bg-[#FF6B35] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#F05A22]"
                        >
                            Request a callback
                        </button>
                    </>
                )}
            </Sheet>
        </section>
    );
}
